import type React from "react"

import { useState } from "react"
import {
  AlertTriangle,
  ChevronDown,
  Plus,
  Tool01,
} from "@untitledui/icons"
import { motion, AnimatePresence } from "motion/react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useConnectedBodyshop } from "@/lib/api/use-teams"
import { teamsApi } from "@/lib/api/teams"
import { ApiError } from "@/lib/api/client"
import { queryKeys } from "@/lib/api/query-keys"
import { useOrganization } from "@/lib/organization/organization-context"
import { useTeamPermissions } from "@/lib/team/use-team-permissions"
import { TeamMembersTableMini } from "@/components/settings/team/team-members-table-mini"
import { DesignSelect } from "@/components/ui/design-select"
import { US_STATES } from "@/components/ui/us-state-select"
import { cx } from "@/lib/utils/cx"

// Settings → Body Shops. For a single dealership user, this shows their
// connected body shop using the SAME expandable-card vocabulary the
// org-chart uses for groups. Empty state stays inline (no popup): the
// card itself is the form for connecting a new shop.
interface BodyShopsSettingsProps {
  dealershipId?: number | string | null
  dealershipName?: string
}

export function BodyShopsSettings({
  dealershipId: dealershipIdProp,
  dealershipName: dealershipNameProp,
}: BodyShopsSettingsProps = {}) {
  const ctx = useOrganization()
  const dealershipId = dealershipIdProp ?? ctx.activeDealershipId
  const dealershipName =
    dealershipNameProp ?? ctx.activeDealership?.name ?? "your dealership"
  const dealershipIdStr = dealershipId != null ? String(dealershipId) : null
  const numericDealershipId = dealershipId != null ? Number(dealershipId) : null

  const { data: connectedBodyshop, isLoading } = useConnectedBodyshop(dealershipIdStr)
  const perms = useTeamPermissions(numericDealershipId)

  // dealershipName retained as prop for parents that want to pass it,
  // but no longer rendered as an inner header — the page-level
  // SectionHeader already shows "{Dealership} · Dealership / Body shop".
  void dealershipName

  return (
    <div>
      <p className="text-[12.5px] text-zinc-500 mb-4">
        Where this dealership sends estimates for repair.
      </p>
      <div>
        {isLoading ? (
          <p className="text-[13px] text-zinc-500 py-6">Loading…</p>
        ) : connectedBodyshop ? (
          <ConnectedShopCard
            teamId={Number(connectedBodyshop.id)}
            name={connectedBodyshop.name}
            city={connectedBodyshop.city}
            state={connectedBodyshop.state}
            address={connectedBodyshop.address}
            phone={connectedBodyshop.phone}
          />
        ) : (
          <ConnectInlineCard
            dealershipTeamId={numericDealershipId}
            canConnect={perms.canManageMembers}
          />
        )}
      </div>
    </div>
  )
}

function ConnectedShopCard({
  teamId,
  name,
  city,
  state,
  address,
  phone,
}: {
  teamId: number
  name: string
  city?: string
  state?: string
  address?: string
  phone?: string
}) {
  const [expanded, setExpanded] = useState(true)
  const cs = [city, state].filter(Boolean).join(", ")
  const fullAddress = [address, cs].filter(Boolean).join(" · ")

  return (
    <div className="rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50/40 to-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full px-4 py-3.5 flex items-center gap-3 text-left hover:bg-amber-50/30 transition-colors duration-150"
      >
        <div className="size-11 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center shrink-0 shadow-md shadow-amber-600/25">
          <Tool01 className="size-5 text-white" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-zinc-900 truncate">{name}</p>
          {(fullAddress || phone) && (
            <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
              {[fullAddress, phone].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        <ChevronDown
          className={cx(
            "size-4 text-zinc-400 shrink-0 transition-transform duration-200",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-amber-200/40 px-4 py-4 bg-white">
              <TeamMembersTableMini
                teamId={teamId}
                teamName={name}
                teamType="Bodyshop"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function ConnectInlineCard({
  dealershipTeamId,
  canConnect,
}: {
  dealershipTeamId: number | null
  canConnect: boolean
}) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [gmEmail, setGmEmail] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<{ name?: string; gmEmail?: string }>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (dealershipTeamId == null) return
    // BE invariant (team.service.ts:437): generalManagerEmail is required
    // whenever the named body shop doesn't already exist. This form always
    // creates a new shop (no existing-shop picker), so treat GM email as
    // required up front rather than waiting for the BE 400.
    const fieldErrors: typeof errors = {}
    if (!name.trim()) fieldErrors.name = "Body shop name is required"
    if (!gmEmail.trim()) {
      fieldErrors.gmEmail = "GM email is required to create a new body shop"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gmEmail.trim())) {
      fieldErrors.gmEmail = "Enter a valid email"
    }
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      await teamsApi.connectBodyshop(dealershipTeamId, {
        bodyshopName: name.trim(),
        generalManagerEmail: gmEmail.trim(),
      })
      // Connect creates a NEW Bodyshop team and stamps the user as a member,
      // so both caches need to refresh: the connected-bodyshop query so this
      // card flips to "connected", and the global teams cache so the team
      // switcher picks up the new shop.
      queryClient.invalidateQueries({
        queryKey: queryKeys.teams.connectedBodyshop(String(dealershipTeamId)),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all })
      toast.success(`${name.trim()} connected`)
      setOpen(false)
      setName("")
      setGmEmail("")
      setCity("")
      setState("")
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Couldn't connect body shop"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <div className="rounded-2xl border border-dashed border-amber-200/70 bg-amber-50/30 px-4 py-3 flex items-center gap-3">
        <div className="size-7 rounded-lg bg-white grid place-items-center shrink-0 shadow-sm">
          <AlertTriangle className="size-4 text-amber-600" aria-hidden />
        </div>
        <p className="text-[13px] font-semibold text-zinc-700 flex-1 truncate">
          No body shop connected — estimates can't ship until one is wired up
        </p>
        {canConnect && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold text-zinc-700 border border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 transition-colors duration-150 shrink-0"
          >
            <Plus className="size-3" aria-hidden />
            Connect a body shop
          </button>
        )}
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-amber-200/70 bg-amber-50/30 px-4 py-4 space-y-3"
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700">
        Connect a body shop
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
        <div className="space-y-1">
          <label className="flex items-center gap-1 text-[12px] font-semibold text-zinc-700">
            Body shop name
            <span className="text-red-500" aria-hidden>*</span>
          </label>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) setErrors({})
            }}
            placeholder="e.g. Elite Auto Body"
            maxLength={255}
            className={cx(
              "h-11 w-full rounded-xl border bg-white px-3.5 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150 focus:border-zinc-400 focus:ring-4 focus:ring-zinc-200/50",
              errors.name ? "border-red-300" : "border-zinc-200",
            )}
            autoFocus
          />
          {errors.name && (
            <p className="text-[11.5px] text-red-600">{errors.name}</p>
          )}
        </div>
        <div className="space-y-1">
          <label className="flex items-center gap-1 text-[12px] font-semibold text-zinc-700">
            GM email
            <span className="text-red-500" aria-hidden>*</span>
          </label>
          <input
            type="email"
            value={gmEmail}
            onChange={(e) => {
              setGmEmail(e.target.value)
              if (errors.gmEmail) setErrors((er) => ({ ...er, gmEmail: undefined }))
            }}
            placeholder="manager@shop.com"
            maxLength={255}
            className={cx(
              "h-11 w-full rounded-xl border bg-white px-3.5 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150 focus:border-zinc-400 focus:ring-4 focus:ring-zinc-200/50",
              errors.gmEmail ? "border-red-300" : "border-zinc-200",
            )}
          />
          {errors.gmEmail && (
            <p className="text-[11.5px] text-red-600">{errors.gmEmail}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
        <div className="space-y-1">
          <label className="block text-[12px] font-semibold text-zinc-700">
            City <span className="text-zinc-400 font-normal">(optional)</span>
          </label>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            maxLength={120}
            className="h-11 w-full rounded-xl border border-zinc-200 bg-white px-3.5 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150 focus:border-zinc-400 focus:ring-4 focus:ring-zinc-200/50"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-[12px] font-semibold text-zinc-700">
            State <span className="text-zinc-400 font-normal">(optional)</span>
          </label>
          <DesignSelect.Root value={state} onValueChange={setState}>
            <DesignSelect.Trigger
              className={cx(
                "w-full h-11 rounded-xl border bg-white px-3.5 text-[14px] font-medium",
                state ? "text-zinc-900" : "text-zinc-400",
                "border-zinc-200 hover:border-zinc-300 hover:bg-white",
                "data-[state=open]:bg-white data-[state=open]:border-zinc-300",
              )}
            >
              <DesignSelect.Value placeholder="Pick a state" />
            </DesignSelect.Trigger>
            <DesignSelect.Content>
              {US_STATES.map((s) => (
                <DesignSelect.Item key={s} value={s}>
                  {s}
                </DesignSelect.Item>
              ))}
            </DesignSelect.Content>
          </DesignSelect.Root>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting || dealershipTeamId == null}
          className="h-11 px-4 inline-flex items-center gap-1.5 rounded-xl bg-zinc-900 text-white text-[13.5px] font-semibold hover:bg-black transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Tool01 className="size-4" aria-hidden />
          {submitting ? "Connecting…" : "Connect"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
            setName("")
            setGmEmail("")
            setCity("")
            setState("")
            setErrors({})
          }}
          className="h-11 px-4 rounded-xl border border-zinc-200 bg-white text-[13.5px] font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors duration-150"
        >
          Cancel
        </button>
      </div>
      <p className="text-[11.5px] text-zinc-500">
        We'll email the GM an invite to set up their account. You can add more
        details after the shop is connected.
      </p>
    </form>
  )
}
